import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Plus, Edit, Trash2, UserCircle, FlaskConical, MapPin } from 'lucide-react';
import { Dropdown } from 'primereact/dropdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { FormDialog } from '@/components/ui/form-dialog';
import { PageHeader } from '@/components/ui/page-header';
import { SearchInput } from '@/components/ui/search-input';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { TableRowActions } from '@/components/ui/table-row-actions';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';
import api from '@/lib/api';
import { formatCurrency } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth.store';

interface LocationRef {
  id: string;
  name: string;
}

interface Employee {
  id: string;
  name: string;
  jobTitle: string;
  wage: string | number;
  location?: LocationRef | null;
  locationId?: string | null;
}

interface EmployeesListResponse {
  data: Employee[];
  total: number;
  page: number;
  limit: number;
}

const defaultForm = {
  name: '',
  jobTitle: '',
  wage: '',
  locationId: '' as string,
};

const LIMIT = 20;

export function EmployeesPage() {
  const { t } = useTranslation('employees');
  const { hasPermission } = useAuthStore();
  const canCreate = hasPermission('employees:create') || hasPermission('employees:*');
  const canUpdate = hasPermission('employees:update') || hasPermission('employees:*');
  const canDelete = hasPermission('employees:delete') || hasPermission('employees:*');
  const canLoadLocations =
    hasPermission('locations:read')
    || hasPermission('locations:*')
    || hasPermission('inventory:read')
    || hasPermission('inventory:*');

  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editEmployee, setEditEmployee] = useState<Employee | null>(null);
  const [form, setForm] = useState({ ...defaultForm });
  // POS-I334: inline messages under Name and Job title (blank, too long, or the server's duplicate sentence)
  const [errors, setErrors] = useState<{ name?: string; jobTitle?: string }>({});
  const [deleteConfirm, setDeleteConfirm] = useState<{ open: boolean; id: string; name: string }>({
    open: false,
    id: '',
    name: '',
  });
  const queryClient = useQueryClient();

  const { data: tenantData } = useQuery({
    queryKey: ['tenant-current'],
    queryFn: () => api.get('/tenant').then((r) => r.data),
    staleTime: 30_000,
  });
  const isTestMode: boolean = tenantData?.testMode ?? false;

  useEffect(() => {
    if (dialogOpen) setErrors({});
    if (dialogOpen && editEmployee) {
      setForm({
        name: editEmployee.name ?? '',
        jobTitle: editEmployee.jobTitle ?? '',
        wage: String(editEmployee.wage ?? ''),
        locationId: editEmployee.location?.id ?? editEmployee.locationId ?? '',
      });
    } else if (dialogOpen) {
      setForm({ ...defaultForm });
    }
  }, [dialogOpen, editEmployee]);

  const { data, isLoading } = useQuery<EmployeesListResponse>({
    queryKey: ['employees', search, page],
    queryFn: async () => {
      const params = new URLSearchParams({ page: String(page), limit: String(LIMIT) });
      if (search) params.set('search', search);
      const res = await api.get(`/employees?${params}`);
      return res.data;
    },
  });

  const { data: locationsRaw } = useQuery<LocationRef[]>({
    queryKey: ['locations-list-employees'],
    queryFn: async () => {
      const res = await api.get('/locations');
      const body = res.data;
      return Array.isArray(body) ? body : body?.data ?? [];
    },
    enabled: canLoadLocations,
  });

  const locationOptions = [
    { label: t('locationNone'), value: '' },
    ...(locationsRaw ?? []).map((loc) => ({ label: loc.name, value: loc.id })),
  ];

  const saveMutation = useMutation({
    mutationFn: async () => {
      const wageNum = form.wage === '' ? 0 : Number(form.wage);
      const payload = {
        name: form.name.trim(),
        jobTitle: form.jobTitle.trim(),
        wage: Number.isFinite(wageNum) ? wageNum : 0,
        locationId: form.locationId ? form.locationId : null,
      };
      if (editEmployee) {
        const { data: saved } = await api.patch(`/employees/${editEmployee.id}`, payload);
        return saved;
      }
      const { data: saved } = await api.post('/employees', payload);
      return saved;
    },
    onSuccess: () => {
      toast.success(editEmployee ? t('updated') : t('created'));
      queryClient.invalidateQueries({ queryKey: ['employees'] });
      setDialogOpen(false);
    },
    onError: (error: unknown) => {
      const raw = (error as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message;
      const message = (Array.isArray(raw) ? raw[0] : raw) ?? t('saveFailed');
      const lower = message.toLowerCase();
      if (lower.includes('job title')) setErrors({ jobTitle: message });
      else if (lower.includes('name')) setErrors({ name: message });
      else toast.error(message);
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`/employees/${id}`),
    onSuccess: () => {
      toast.success(t('deleted'));
      queryClient.invalidateQueries({ queryKey: ['employees'] });
    },
    onError: () => toast.error(t('deleteFailed')),
  });

  const employees = data?.data ?? [];
  const total = data?.total ?? 0;

  return (
    <div className="space-y-6 page-enter">
      <PageHeader
        title={t('title')}
        subtitle={t('subtitle')}
        action={canCreate ? (
          <Button variant="gradient" onClick={() => { setEditEmployee(null); setDialogOpen(true); }}>
            <Plus className="me-2 h-4 w-4" />
            {t('addEmployee')}
          </Button>
        ) : undefined}
      />

      {isTestMode && (
        <div className="flex items-center gap-2 rounded-lg border border-warning/30 bg-warning/10 px-4 py-3 text-sm text-warning-text">
          <FlaskConical className="h-4 w-4 shrink-0" />
          <span>{t('testModeBanner')}</span>
        </div>
      )}

      <SearchInput
        placeholder={t('searchPlaceholder')}
        value={search}
        onChange={(value) => { setSearch(value); setPage(1); }}
      />

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <SkeletonTable cols={4} />
          ) : employees.length === 0 ? (
            <EmptyState
              icon={UserCircle}
              title={t('emptyTitle')}
              description={t('emptyDescription')}
              action={canCreate ? {
                label: t('addEmployee'),
                onClick: () => { setEditEmployee(null); setDialogOpen(true); },
              } : undefined}
            />
          ) : (
            <Table mobileCards>
                <THead>
                  <Tr hover={false}>
                    <Th>{t('colName')}</Th>
                    <Th>{t('colJobTitle')}</Th>
                    <Th className="text-end">{t('colWage')}</Th>
                    <Th>{t('colLocation')}</Th>
                    <Th className="text-end">{t('colActions')}</Th>
                  </Tr>
                </THead>
                <TBody>
                  {employees.map((employee) => (
                    <Tr key={employee.id}>
                      <Td className="td-name font-medium text-foreground">{employee.name}</Td>
                      <Td label={t('colJobTitle')} className="text-muted-foreground">{employee.jobTitle}</Td>
                      <Td label={t('colWage')} className="text-end tabular-nums">
                        {formatCurrency(Number(employee.wage ?? 0))}
                      </Td>
                      <Td label={t('colLocation')} className="text-muted-foreground">
                        {employee.location?.name ? (
                          <span className="inline-flex items-center gap-1">
                            <MapPin className="h-3.5 w-3.5 shrink-0" />
                            {employee.location.name}
                          </span>
                        ) : '—'}
                      </Td>
                      <Td className="td-actions">
                        <TableRowActions
                          actions={[
                            {
                              key: 'edit',
                              label: t('editEmployee'),
                              icon: Edit,
                              onSelect: () => { setEditEmployee(employee); setDialogOpen(true); },
                              permission: canUpdate,
                              primary: true,
                            },
                            {
                              key: 'delete',
                              label: t('deleteTitle'),
                              icon: Trash2,
                              onSelect: () => setDeleteConfirm({
                                open: true,
                                id: employee.id,
                                name: employee.name,
                              }),
                              permission: canDelete,
                              variant: 'destructive',
                            },
                          ]}
                        />
                      </Td>
                    </Tr>
                  ))}
                </TBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {total > LIMIT && (
        <PaginationBar
          page={page}
          total={total}
          pageSize={LIMIT}
          onPageChange={setPage}
        />
      )}

      <FormDialog
        open={dialogOpen}
        title={editEmployee ? t('editEmployee') : t('addEmployee')}
        width="32rem"
        submitLabel={editEmployee ? t('editEmployee') : t('addEmployee')}
        isPending={saveMutation.isPending}
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          const errs: { name?: string; jobTitle?: string } = {};
          const name = form.name.trim();
          const job = form.jobTitle.trim();
          if (!name) errs.name = t('errNameRequired');
          else if (name.length > 100) errs.name = t('errNameTooLong');
          if (!job) errs.jobTitle = t('errJobRequired');
          else if (job.length > 100) errs.jobTitle = t('errJobTooLong');
          setErrors(errs);
          if (Object.keys(errs).length) return;
          saveMutation.mutate();
        }}
        onClose={() => setDialogOpen(false)}
      >
        <div>
          <Label htmlFor="employee-name">{t('fieldName')}</Label>
          <Input
            id="employee-name"
            value={form.name}
            maxLength={100}
            aria-invalid={!!errors.name}
            aria-describedby={errors.name ? 'employee-name-error' : undefined}
            onChange={(e) => { setForm({ ...form, name: e.target.value }); if (errors.name) setErrors({ ...errors, name: undefined }); }}
          />
          {errors.name && <p id="employee-name-error" role="alert" className="mt-1 text-xs text-destructive-text">{errors.name}</p>}
        </div>
        <div>
          <Label htmlFor="employee-job-title">{t('fieldJobTitle')}</Label>
          <Input
            id="employee-job-title"
            value={form.jobTitle}
            maxLength={100}
            aria-invalid={!!errors.jobTitle}
            aria-describedby={errors.jobTitle ? 'employee-job-error' : undefined}
            onChange={(e) => { setForm({ ...form, jobTitle: e.target.value }); if (errors.jobTitle) setErrors({ ...errors, jobTitle: undefined }); }}
          />
          {errors.jobTitle && <p id="employee-job-error" role="alert" className="mt-1 text-xs text-destructive-text">{errors.jobTitle}</p>}
        </div>
        <div>
          <Label htmlFor="employee-wage">{t('fieldWage')}</Label>
          <Input
            id="employee-wage"
            type="number"
            min={0}
            step="0.01"
            value={form.wage}
            onChange={(e) => setForm({ ...form, wage: e.target.value })}
          />
        </div>
        {canLoadLocations && (
          <div>
            <Label htmlFor="employee-location">{t('fieldLocation')}</Label>
            <Dropdown
              inputId="employee-location"
              value={form.locationId}
              options={locationOptions}
              onChange={(e) => setForm({ ...form, locationId: (e.value as string) ?? '' })}
              placeholder={t('locationPlaceholder')}
              className="w-full"
            />
          </div>
        )}
      </FormDialog>

      <ConfirmDialog
        open={deleteConfirm.open}
        title={t('deleteTitle')}
        description={t('deleteDescription', { name: deleteConfirm.name })}
        confirmLabel={t('deleteTitle')}
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => {
          deleteMutation.mutate(deleteConfirm.id, {
            onSuccess: () => setDeleteConfirm({ open: false, id: '', name: '' }),
          });
        }}
        onClose={() => setDeleteConfirm({ open: false, id: '', name: '' })}
      />
    </div>
  );
}
